import { useCallback, useMemo, useRef, useState } from 'react';
import { noop } from 'lodash/fp';
import {
  AnimatedPopover,
  Container,
  Content,
  Divider,
  Header,
  Icon,
  InPlaceInput,
  Inline,
  InlineItem,
  LabelText,
  Menu,
  MenuItem,
  MonthPicker,
  Stack,
  Text,
  useEventListener,
  useOnClickOutside,
} from '@kinesis/bungle';
import { Layout } from '@/components/layout';
import { GHGProtocolIcon } from '@/components/ghg-protocol-icon';
import { Disclosure, DisclosurePeriod, Place } from '@/types';
import {
  disclosureMonthFromIndex,
  disclosureMonthIndex,
  renderDisclosurePeriod,
} from '@/data/disclosure-month';
import { flattenPlaceTree } from '@/data/places';

// Editable disclosure header. Store-less: takes the current `disclosure`
// and an `onChange` callback that fires on every edit (name keystroke or
// period pick) — the page owns the `localDisclosure` + `hasChanges` state
// and decides when to call the update mutation. Mirrors suit's
// `components/disclosure-header/disclosure-header.tsx`, minus the scenario
// popover (square has no scenario UI in Phase 2).
//
// Two menu rows, mirroring suit's split:
//   1. Disclosure-type label (GHG icon + "Community disclosure") carrying the
//      show/hide-configuration toggle on the right. Clicking the row flips
//      `showConfiguration` (owned by the page via a `?show=` URL param) and
//      collapses/expands the configuration rows below; the right-aligned
//      label flips between "Show configuration" and "Hide configuration".
//   2. Period editor (calendar icon + period text or "Configure period"
//      placeholder) — a configuration row: shown only when
//      `showConfiguration` is on. Clickable; opens an AnimatedPopover over a
//      MonthPicker.
//   3. Place-scope editor (map icon + the scoped place's name, or "Whole
//      inventory") — a configuration row, shown only when the inventory has
//      places to scope to. Opens a popover listing "Whole inventory" + the
//      place hierarchy; picking one scopes the whole report to that place's
//      subtree. `places` is passed in by the page (components don't fetch).
//
// The editors live inline here rather than as their own components since
// there's a single consumer; extract when a second disclosure type lands.

type DisclosureHeaderProps = {
  disclosure: Disclosure;
  onChange: (v: Disclosure) => void;
  places?: Place[];
  showConfiguration?: boolean;
  toggleConfiguration?: () => void;
};

type PeriodMenuItemProps = {
  period: DisclosurePeriod | undefined;
  onChange: (v: DisclosurePeriod) => void;
};

const PeriodMenuItem = (props: PeriodMenuItemProps) => {
  const { period, onChange } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  const date = useMemo(
    () =>
      period
        ? new Date(period.year, disclosureMonthIndex(period.month))
        : undefined,
    [period],
  );

  const handlePick = useCallback(
    (picked: Date) => {
      onChange({
        year: picked.getFullYear(),
        month: disclosureMonthFromIndex(picked.getMonth()),
      });
      close();
    },
    [onChange, close],
  );

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    useCallback(() => close(), [close]),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  return (
    <>
      <MenuItem
        ref={ref}
        icon={<Icon magnitude='medium' type='calendar' />}
        content={
          period ? (
            <Text>{renderDisclosurePeriod(period)}</Text>
          ) : (
            <Text tone='secondary'>Configure period</Text>
          )
        }
        onClick={toggle}
      />
      <AnimatedPopover
        stopPropagation
        justify='stretch'
        open={isOpen}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <Content
          ref={popoverRef}
          borderRadiusSize='large'
          border
          shadow
          background='white'
          padding='medium'
        >
          <Inline alignY='top' space='large'>
            <InlineItem sizing='fill-container'>
              <Content padding='small'>
                <Stack space='medium'>
                  <LabelText>Period end</LabelText>
                  <Text tone='secondary'>
                    Select the ending month for this disclosure. The disclosure
                    period is 12 months up to and including the selected month.
                  </Text>
                </Stack>
              </Content>
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <MonthPicker value={date} onChange={handlePick} />
            </InlineItem>
          </Inline>
        </Content>
      </AnimatedPopover>
    </>
  );
};

type PlaceOptionProps = {
  label: string;
  depth: number;
  selected: boolean;
  onClick: () => void;
};

// One row in the place-scope popover: an indented label (depth in the
// hierarchy) with a check on the current scope. Same Inline split as the
// header's show/hide row (fill-container label + fit-content marker).
const PlaceOption = (props: PlaceOptionProps) => {
  const { label, depth, selected, onClick } = props;
  return (
    <MenuItem
      onClick={onClick}
      content={
        <Inline>
          <InlineItem sizing='fill-container'>
            <div style={{ paddingLeft: `${depth * 16}px` }}>
              <Text>{label}</Text>
            </div>
          </InlineItem>
          {selected && (
            <InlineItem sizing='fit-content'>
              <Icon magnitude='small' type='check' />
            </InlineItem>
          )}
        </Inline>
      }
    />
  );
};

type PlaceMenuItemProps = {
  places: Place[];
  value: number | undefined;
  onChange: (place: number | undefined) => void;
};

// Place-scope editor: a config row showing the current scope (the scoped
// place's name, or "Whole inventory" when unset) that opens a popover
// listing "Whole inventory" + the inventory's place hierarchy (indented by
// depth). Picking an entry scopes the whole report to that place's subtree;
// "Whole inventory" clears the scope. Mirrors PeriodMenuItem's popover shell.
const PlaceMenuItem = (props: PlaceMenuItemProps) => {
  const { places, value, onChange } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  const flat = useMemo(() => flattenPlaceTree(places), [places]);
  const selected = useMemo(
    () =>
      value !== undefined ? places.find((p) => p.id === value) : undefined,
    [places, value],
  );

  const pick = useCallback(
    (place: number | undefined) => {
      onChange(place);
      close();
    },
    [onChange, close],
  );

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    useCallback(() => close(), [close]),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  return (
    <>
      <MenuItem
        ref={ref}
        icon={<Icon magnitude='medium' type='map-filled' />}
        content={<Text>{selected ? selected.name : 'Whole inventory'}</Text>}
        onClick={toggle}
      />
      <AnimatedPopover
        stopPropagation
        justify='stretch'
        open={isOpen}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <Content
          ref={popoverRef}
          borderRadiusSize='large'
          border
          shadow
          background='white'
          padding='small'
        >
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <Menu>
              <PlaceOption
                label='Whole inventory'
                depth={0}
                selected={value === undefined}
                onClick={() => pick(undefined)}
              />
              {flat.map(({ place, depth }) => (
                <PlaceOption
                  key={place.id}
                  label={place.name}
                  depth={depth + 1}
                  selected={place.id === value}
                  onClick={() => pick(place.id)}
                />
              ))}
            </Menu>
          </div>
        </Content>
      </AnimatedPopover>
    </>
  );
};

const DisclosureHeader = (props: DisclosureHeaderProps) => {
  const {
    disclosure,
    onChange,
    places = [],
    showConfiguration = true,
    toggleConfiguration = noop,
  } = props;

  const onChangeName = useCallback(
    (v: string) => {
      onChange({ ...disclosure, name: v });
    },
    [disclosure, onChange],
  );

  const onChangePeriod = useCallback(
    (period: DisclosurePeriod) => {
      onChange({
        ...disclosure,
        definition: { ...disclosure.definition, period },
      });
    },
    [disclosure, onChange],
  );

  // A place scope is just another field on the definition (like the period),
  // so picking one re-runs the simulate queries and re-scopes the report
  // live. `undefined` is the whole-inventory default; it drops off the wire
  // (JSON omits undefined), so village reads it back as no scope.
  const onChangePlace = useCallback(
    (place: number | undefined) => {
      onChange({
        ...disclosure,
        definition: { ...disclosure.definition, place },
      });
    },
    [disclosure, onChange],
  );

  // Same structure as suit's disclosure header: a bungle `Container >
  // Header` sitting in the disclosure grid's full-width section, with the
  // content offset left by the sidebar width (239px) so the title lines
  // up with the body beneath it.
  return (
    <Container>
      <Header border>
        <Content background='white' padding='none'>
          <div style={{ paddingLeft: '239px' }}>
            <Layout>
              <InPlaceInput
                value={disclosure.name}
                placeholder='Disclosure name'
                onChange={onChangeName}
                variant='title'
              />
              <Divider />
              <Menu>
                <MenuItem
                  icon={<GHGProtocolIcon />}
                  content={
                    <Inline>
                      <InlineItem sizing='fill-container'>
                        <Text>Community disclosure</Text>
                      </InlineItem>
                      <InlineItem sizing='fit-content'>
                        <Text tone='secondary'>
                          {showConfiguration
                            ? 'Hide configuration'
                            : 'Show configuration'}
                        </Text>
                      </InlineItem>
                    </Inline>
                  }
                  onClick={toggleConfiguration}
                />
                {showConfiguration && (
                  <PeriodMenuItem
                    period={disclosure.definition.period}
                    onChange={onChangePeriod}
                  />
                )}
                {showConfiguration && places.length > 0 && (
                  <PlaceMenuItem
                    places={places}
                    value={disclosure.definition.place}
                    onChange={onChangePlace}
                  />
                )}
              </Menu>
            </Layout>
          </div>
        </Content>
      </Header>
    </Container>
  );
};

export type { DisclosureHeaderProps };
export { DisclosureHeader };
